/**
 * Schedule: the board, its panels, and an appointment's lifecycle.
 *
 * `schedule.panel.*` and the event LIFECYCLE vocabulary are what a clinician
 * reads about one appointment. The appointment-STATUS words that used to sit
 * beside them — `status.planned` and its four siblings — belonged to the
 * practitioner day list's own projection and went with that surface; the
 * board's lifecycle words are `schedule.lifecycle.*`.
 *
 * Composed into the public catalogue by `catalogue.en.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const schedule = {
  'schedule.selfBooking.title': 'Patient self-booking',
  'schedule.selfBooking.doctor': 'Doctor',
  'schedule.selfBooking.enabled': 'Allowed',
  'schedule.selfBooking.disabled': 'Not allowed',
  'schedule.selfBooking.allowDoctor': 'Allow self-booking with {name}',
  'schedule.selfBooking.inactiveDoctor': 'Inactive doctor',
  'schedule.selfBooking.empty': 'No doctors available',
  'schedule.selfBooking.saving': 'Saving permission',
  'schedule.selfBooking.saved': 'Permission saved',
  'schedule.selfBooking.stale': 'This permission changed. Reload before trying again.',
  'schedule.selfBooking.failed': 'Could not save or reload the permission.',
  'schedule.selfBooking.reload': 'Reload permissions',
  'schedule.title': 'Schedule',
  'schedule.today': 'Today',
  'schedule.invalidDate': 'That date is not valid',
  'schedule.invalidDateDetail': 'Use the date controls to choose a day.',
  'schedule.columnPatient': 'Patient',
  'schedule.openPatient': 'Open {name}',
  'schedule.patientCode': 'Patient code',
  'schedule.availability.available': 'Available',
  'schedule.availability.noAvailability': 'No schedulable availability',
  'schedule.resourceType.practitioner': 'Practitioner',
  'schedule.resourceType.patient': 'Patient',
  'schedule.resourceType.room': 'Room',
  'schedule.resourceType.equipment': 'Equipment',
  'schedule.resourceType.location': 'Location',
  'schedule.resourceType.organization': 'Organization',
  'schedule.resourceType.servicePoint': 'Service point',
  'schedule.resourceType.other': 'Other',
  'schedule.busyKind.appointment': 'Appointment',
  'schedule.busyKind.hold': 'Hold',
  'schedule.busyKind.block': 'Blocked time',
  'schedule.busyKind.busyElsewhere': 'Busy elsewhere',
  'schedule.lifecycle.held': 'Held',
  'schedule.lifecycle.planned': 'Planned',
  'schedule.lifecycle.fulfilled': 'Fulfilled',
  'schedule.lifecycle.cancelled': 'Cancelled',
  'schedule.lifecycle.noShow': 'Did not attend',
  'schedule.lifecycle.enteredInError': 'Entered in error',
  'schedule.dayStatus.planned': 'Upcoming',
  'schedule.dayStatus.started': 'Checked in',
  'schedule.dayStatus.completed': 'Completed',
  'schedule.dayStatus.cancelled': 'Cancelled',
  'schedule.dayStatus.noShow': 'Did not attend',
  'schedule.candidateReason.earlier': 'Earlier option',
  'schedule.candidateReason.sameSite': 'Same site',
  'schedule.candidateReason.preferredResource': 'Preferred resource',
  'schedule.candidateReason.smallerPractitionerGap': 'Smaller practitioner gap',
  'schedule.command.applied': 'Applied',
  'schedule.command.replayed': 'Already applied',
  'schedule.command.overlap': 'This time overlaps another schedule item.',
  'schedule.command.capacityExceeded': 'The selected resource has no remaining capacity.',
  'schedule.command.outsideAvailability': 'The selected time is outside availability.',
  'schedule.command.revisionMismatch': 'The schedule changed while this action was open.',
  'schedule.command.holdExpired': 'This hold has expired.',
  'schedule.command.forbidden': 'You do not have permission to change this schedule.',
  'schedule.command.invalidCommand': 'This schedule action is no longer valid.',
  'schedule.command.missing': 'This schedule item no longer exists. Reload the board.',
  'schedule.command.rejected': 'The schedule action was rejected.',
  'schedule.command.conflict': 'The schedule changed before this action could be applied.',
  'schedule.command.failed': 'The schedule action could not be completed.',
  'schedule.command.patientUnavailable': 'The patient is unavailable at this time.',
  'schedule.command.outsidePatientStay': 'This time is outside the patient travel period.',

  'schedule.nav.label': 'Schedule sections',
  'schedule.nav.calendar': 'Calendar',
  'schedule.nav.calendarDescription': 'Plan appointments and blocked time',
  'schedule.nav.availability': 'Availability',
  'schedule.nav.availabilityDescription': 'Weekly schedule and date changes',
  'schedule.nav.resources': 'Resources',
  'schedule.nav.resourcesDescription': 'Places, organizations, external resources',
  'schedule.nav.visitTypes': 'Encounter types',
  'schedule.nav.visit-typesDescription': 'Durations and required resources',
  'schedule.find.action': 'Find time',
  'schedule.block.action': 'Block time',
  'schedule.block.create': 'Create block',
  'schedule.appointment.action': 'New appointment',
  'schedule.appointment.create': 'Create appointment',
  'schedule.appointment.visitReason': 'Visit reason',

  'schedule.settings.loading': 'Loading schedule settings',
  'schedule.settings.loadFailed': 'Schedule settings could not be loaded.',
  'schedule.settings.save': 'Save',
  'schedule.settings.cancel': 'Cancel',
  'schedule.settings.saveFailed': 'The change could not be saved.',
  'schedule.settings.editNamed': 'Edit {name}',
  'schedule.settings.actions': 'Actions',
  'schedule.settings.active': 'Active',
  'schedule.settings.inactive': 'Inactive',
  'schedule.resources.title': 'Resources',
  'schedule.resources.branch': 'Branch',
  'schedule.branches.all': 'All branches',
  'schedule.branches.workplaceRequired': 'Create an active workplace for this clinician at the branch before adding working hours.',
  'schedule.command.workplaceRequired': 'Choose the clinician\'s workplace for this appointment.',
  'schedule.command.workplaceUnavailable': 'This workplace is not available for the selected clinician, branch or appointment time.',
  'schedule.command.workplaceChanged': 'The workplace has changed. Refresh it and confirm your choice.',
  'schedule.branches.overlap': 'Working hours overlap between branches. Correct the availability intervals.',
  'schedule.branches.globalBlock': 'These hours overlap a block that applies to all branches.',
  'schedule.branches.clearDescription': 'Clear hours and dated changes for this branch only. Global blocks, personal commitments and other branches will remain unchanged.',
  'schedule.resources.branchRequired': 'Select a branch',
  'schedule.resources.branchUnavailable': 'This branch is no longer available. Select another branch.',
  'schedule.resources.branchInUse': 'Move or cancel the resource\'s appointments before changing its branch.',
  'schedule.resources.subtitle': 'Named places, organizations, equipment and external practitioners.',
  'schedule.resources.name': 'Name',
  'schedule.resources.type': 'Type',
  'schedule.resources.location': 'Location',
  'schedule.resources.grid': 'Grid',
  'schedule.resources.state': 'State',
  'schedule.resources.minutes': '{count} min',
  'schedule.resources.preBuffer': 'Buffer before, min',
  'schedule.resources.postBuffer': 'Buffer after, min',
  'schedule.resources.capabilities': 'Capabilities',
  'schedule.resources.addTitle': 'Add resource',
  'schedule.resources.editTitle': 'Edit resource',
  'schedule.resources.detailsTitle': 'Resource details',
  'schedule.resources.openNamed': 'Open resource {name}',
  'schedule.resources.empty': 'No standalone resources are configured.',
  'schedule.resources.availabilityDefaults': 'Availability defaults',
  'schedule.resources.address': 'Address',
  'schedule.resources.addressLine1': 'Address',
  'schedule.resources.addressLine2': 'Address details',
  'schedule.resources.city': 'City',
  'schedule.resources.region': 'State or region',
  'schedule.resources.postalCode': 'Postal code',
  'schedule.resources.country': 'Country code',
  'schedule.resources.contact': 'Contact',
  'schedule.resources.phone': 'Phone',
  'schedule.resources.email': 'Email',
  'schedule.resources.notes': 'Notes',
  'schedule.resources.metadata': 'Metadata (JSON)',
  'schedule.resources.booking': 'Appointment booking',
  'schedule.availability.title': 'Availability',
  'schedule.availability.subtitle': 'Availability profiles for practitioners, patients and standalone resources.',
  'schedule.availability.resource': 'Resource',
  'schedule.availability.target': 'Target',
  'schedule.availability.timeZone': 'Time zone',
  'schedule.availability.useClinicTimeZone': 'Use the clinic time zone',
  'schedule.availability.useClinicCalendar': 'Use the clinic working calendar',
  'schedule.availability.calendar': 'Working calendar',
  'schedule.availability.calendarNone': 'Not applied',
  'schedule.availability.booking': 'Booking',
  'schedule.availability.bookable': 'Bookable',
  'schedule.availability.openForBooking': 'Open for booking',
  'schedule.availability.informational': 'Informational',
  'schedule.availability.profilesEmpty': 'No availability profiles are configured.',
  'schedule.availability.openNamed': 'Open availability for {name}',
  'schedule.availability.appointment': 'Appointment',
  'schedule.availability.busy': 'Other commitment',
  'schedule.availability.hold': 'Reserved (pending)',
  'schedule.availability.dayAvailableHours': '{date}: available {hours}',
  'schedule.availability.dayClosed': '{date}: unavailable',
  'schedule.availability.add': 'Add',
  'schedule.availability.menuExtra': 'Add hours on this date',
  'schedule.availability.menuBlock': 'Mark unavailable',
  'schedule.availability.editDay': 'Edit this day',
  'schedule.availability.editDayTitle': 'Open hours on {date}',
  'schedule.availability.resetDay': 'Return to the weekly schedule',
  'schedule.availability.followsWeekly': 'This day follows the weekly schedule.',
  'schedule.availability.differsWeekly': 'This day differs from the weekly schedule.',
  'schedule.availability.savePartlyApplied':
    'Part of this day was saved before the rest was refused. What the schedule holds has been reread, so Save now writes only what is still missing.',
  'schedule.availability.closedAllDay': 'Closed all day',
  'schedule.availability.addInterval': 'Add hours',
  'schedule.availability.removeInterval': 'Remove {hours}',
  'schedule.availability.legend': 'Legend',
  'schedule.availability.legendAvailability': 'Availability',
  'schedule.availability.legendOccupancy': 'Busy time',
  'schedule.availability.usually': 'Usually: {pattern}',
  'schedule.availability.effectOpen': 'Open on {date}: {hours}',
  'schedule.availability.effectClosed': 'No open hours remain on {date}.',
  'schedule.availability.noEffect': 'This change does not alter the open hours on {date}.',
  'schedule.availability.clear': 'Clear',
  'schedule.availability.restore': 'Restore',
  'schedule.availability.presetSunThu': 'Sun–Thu',
  'schedule.availability.presetMonFri': 'Mon–Fri',
  'schedule.availability.presetEveryDay': 'Every day',
  'schedule.availability.notConfigured': 'Availability is not configured.',
  'schedule.availability.createProfile': 'Configure availability',
  'schedule.availability.profileSettings': 'Availability settings',
  'schedule.availability.settingsHint': 'Time zone and working calendar — {name}’s own scheduling settings, not a view option',
  'schedule.availability.settingsOwnerNote': 'These settings belong to {name}. The time zone and the working calendar govern everything scheduled here — not just this calendar view.',
  'schedule.availability.state': 'State',
  'schedule.availability.stateHint': 'Inactive suspends the whole profile: it offers no hours and cannot be booked until it is active again. The weekly schedule and date changes are kept.',
  'schedule.availability.days': 'Days',
  'schedule.availability.followWorkingCalendar': 'Skip non-working days in the selected calendar',
  'schedule.availability.day': 'Day',
  'schedule.availability.hours': 'Hours',
  'schedule.availability.openEnded': 'No end date',
  'schedule.availability.reason': 'Reason',
  'schedule.availability.block': 'Unavailable',
  'schedule.availability.extra': 'Added hours',
  'schedule.availability.start': 'Start',
  'schedule.availability.end': 'End',
  'schedule.availability.validFrom': 'Valid from',
  'schedule.availability.validTo': 'Valid to',
  'schedule.availability.addRule': 'Add weekly hours',
  'schedule.availability.editRule': 'Edit weekly hours',
  'schedule.availability.addExtraTitle': 'Add hours on a date',
  'schedule.availability.editExtraTitle': 'Edit added hours',
  'schedule.availability.addBlockTitle': 'Mark time unavailable',
  'schedule.availability.editBlockTitle': 'Edit unavailable time',
  'schedule.availability.sunday': 'Sunday',
  'schedule.availability.monday': 'Monday',
  'schedule.availability.tuesday': 'Tuesday',
  'schedule.availability.wednesday': 'Wednesday',
  'schedule.availability.thursday': 'Thursday',
  'schedule.availability.friday': 'Friday',
  'schedule.availability.saturday': 'Saturday',
  'schedule.nav.workingCalendar': 'Working calendar',
  'schedule.nav.working-calendarDescription': 'Working and non-working days',

  'schedule.canvas.scaleLabel': 'Calendar scale',
  'schedule.canvas.contextMenu': 'Calendar actions',
  'schedule.canvas.scale.years': 'Years',
  'schedule.canvas.scale.year': 'Year',
  'schedule.canvas.scale.month': 'Month',
  'schedule.canvas.scale.week': 'Week',
  'schedule.canvas.scale.day': 'Day',
  'schedule.canvas.fullDay': 'Full day',
  'schedule.canvas.nowLine': 'Now line',
  'schedule.canvas.nowLineHint': "Draw the clinic clock's current time as a line across the lanes.",
  'schedule.canvas.navigation': 'Calendar navigation',
  'schedule.canvas.todayHint': 'Go to today',
  'schedule.canvas.previousDay': 'Previous day',
  'schedule.canvas.nextDay': 'Next day',
  // The day on a phone: the two readings, the agenda's name and the picker.
  'schedule.handheld.view': 'View',
  'schedule.handheld.agenda': 'Agenda',
  'schedule.handheld.board': 'Board',
  'schedule.handheld.agendaLabel': 'Agenda for {day}',
  'schedule.handheld.agendaEmpty': 'Nothing on this day',
  'schedule.handheld.time': '{from}–{to}',
  'schedule.handheld.pickDate': 'Go to a date',
  'schedule.canvas.previousWeek': 'Previous week',
  'schedule.canvas.nextWeek': 'Next week',
  'schedule.canvas.previousMonth': 'Previous month',
  'schedule.canvas.nextMonth': 'Next month',
  'schedule.canvas.previousYear': 'Previous year',
  'schedule.canvas.nextYear': 'Next year',
  'schedule.canvas.previousYears': 'Previous years',
  'schedule.canvas.nextYears': 'Next years',
  'schedule.canvas.zoomLabel': 'Time detail',
  'schedule.canvas.slotOption': '{minutes} min',
  'schedule.canvas.zoomHint': 'Interval size on the time grid. Ctrl+Shift+scroll also changes it.',
  'schedule.canvas.range': '{from} – {to}',
  'schedule.canvas.bandName': '{from}–{to}, {label}',
  'schedule.canvas.monthGridLabel': 'Days of {month}',
  'schedule.canvas.yearGridLabel': 'Months of {year}',
  'schedule.canvas.yearsGridLabel': 'Years {from} to {to}',
  'schedule.canvas.weekGridLabel': 'Days of the week',
  'schedule.canvas.dayGridLabel': 'Hours of the day',
  'schedule.canvas.intervalTrack': 'Timeline for {date}',
  'schedule.canvas.openMonth': 'Open {month}',
  'schedule.canvas.openYear': 'Open {year}',
  'schedule.canvas.openWeek': 'Open week',
  'schedule.canvas.openDay': 'Open day',

  'schedule.timeObject.selected': 'Selected object',
  'schedule.timeObject.newInterval': 'New interval',
  'schedule.timeObject.chooseType': 'Choose what this interval means. Its type is never inferred from the background.',
  'schedule.timeObject.editTitle': 'Edit time object',
  'schedule.timeObject.edit': 'Edit',
  'schedule.timeObject.duplicate': 'Duplicate',
  'schedule.timeObject.delete': 'Delete',
  'schedule.timeObject.deleteTitle': 'Delete time object',
  'schedule.timeObject.deleteAvailabilityTitle': 'Remove this availability object?',
  'schedule.timeObject.deleteAvailabilityDescription': 'The record is disabled and remains in audit history. You can restore it from Records.',
  'schedule.timeObject.type': 'Type',
  'schedule.timeObject.intervalType': 'Interval type',
  'schedule.timeObject.name': 'Name',
  'schedule.timeObject.duration': 'Duration',
  'schedule.timeObject.recurrence': 'Repeat',
  'schedule.timeObject.recurrence.none': 'Does not repeat',
  'schedule.timeObject.recurrence.daily': 'Every day',
  'schedule.timeObject.recurrence.weekly': 'Weekly',
  'schedule.timeObject.kind.busy': 'Busy time',
  'schedule.timeObject.kind.externalAppointment': 'Appointment elsewhere',
  'schedule.timeObject.kind.travel': 'Travel',
  'schedule.timeObject.kind.transfer': 'Transfer',
  'schedule.timeObject.kind.personal': 'Personal',
  'schedule.timeObject.kind.other': 'Other commitment',
  'schedule.timeObject.applyTo': 'Apply this change to',
  'schedule.timeObject.scope.occurrence': 'Only this occurrence',
  'schedule.timeObject.scope.following': 'This and following occurrences',
  'schedule.timeObject.scope.series': 'The future series',
  'schedule.timeObject.ownerTimeZone': 'Civil times follow the owner’s time zone: {zone}.',
  'schedule.timeObject.overlapChooser': 'Objects overlap here. Choose the one to inspect:',
  'schedule.timeObject.snap': 'Edit snap',
  'schedule.timeObject.undo': 'Undo last move',
  'schedule.timeObject.reverted': 'The server refused the change. The object was restored to its confirmed time.',
  'schedule.timeObject.saveFailed': 'The time object could not be saved.',
  'schedule.timeObject.clearTitle': 'Clear editable calendar configuration?',
  'schedule.timeObject.clearDescription': 'This disables weekly hours, date changes and commitment series for this owner.',
  'schedule.timeObject.clearRules': 'Weekly availability rules',
  'schedule.timeObject.clearOverrides': 'Date overrides',
  'schedule.timeObject.clearSeries': 'Commitment series',
  'schedule.timeObject.clearKeepsAppointments': 'Appointments, holds and audit history are not deleted.',
  'schedule.timeObject.copy': 'Copy',
  'schedule.timeObject.cut': 'Cut',
  'schedule.timeObject.paste': 'Paste here',
  'schedule.timeObject.addHere': 'Add an interval here',
  'schedule.timeObject.newIntervalRange': '{date}, {from}–{to}',
  'schedule.timeObject.clearVisible': 'Clear what is shown',
  'schedule.timeObject.clearVisibleTitle': 'Clear the dated records shown here?',
  'schedule.timeObject.clearVisibleRange': '{from} – {to}',
  'schedule.timeObject.clearVisibleConfirm': 'Clear these records',
  'schedule.timeObject.clearVisibleKeeps': 'Weekly hours and repeating commitments are standing records and are left alone. Appointments, holds and audit history are not deleted.',
  'schedule.timeObject.clearCommitments': 'One-off commitments',
  'schedule.timeObject.clearAll': 'Clear the whole calendar',
  'schedule.timeObject.clearConfirm': 'Clear configuration',
  'schedule.timePlanner.title': 'Team time',
  'schedule.timePlanner.subtitle': 'Put people and resources on one time surface and find a continuous window usable by all of them.',
  'schedule.timePlanner.searchOwners': 'Find people or resources',
  'schedule.timePlanner.owners': 'Selected calendars',
  'schedule.timePlanner.chooseOwners': 'Choose one or more calendars to compare.',
  'schedule.timePlanner.loading': 'Loading calendar projection',
  'schedule.timePlanner.loadFailed': 'The calendar projection could not be loaded.',
  'schedule.timePlanner.lane': 'Time objects for {name}',
  'schedule.timePlanner.candidates': 'Mutually available time',
  'schedule.timePlanner.searching': 'Finding common time',
  'schedule.timePlanner.searchFailed': 'Common time could not be calculated.',
  'schedule.timePlanner.noCandidates': 'No continuous window fits every selected calendar in this day.',
  'schedule.timePlanner.object.availability': 'Available',
  'schedule.timePlanner.object.override': 'Date change',
  'schedule.timePlanner.object.commitment': 'Commitment',
  'schedule.timePlanner.object.appointment': 'Appointment',
  'schedule.timePlanner.object.hold': 'Hold',
  'schedule.timePlanner.object.constraint': 'Outside travel period',

  'schedule.workingCalendar.title': 'Working calendar',
  'schedule.workingCalendar.subtitle': 'Country or subdivision working days, at every zoom from years to hours.',
  'schedule.workingCalendar.calendar': 'Calendar',
  'schedule.workingCalendar.loading': 'Loading working calendar',
  'schedule.workingCalendar.empty': 'No working calendars are imported.',
  'schedule.workingCalendar.emptyDetail':
    'Import a country calendar before configuring clinic or resource working hours from it.',
  'schedule.workingCalendar.sourceNotice':
    'Loaded from {source} ({version}). Manual day changes are saved in Schedule.',
  'schedule.workingCalendar.readOnlyNotice': 'You can view this calendar but cannot edit country-level days.',
  'schedule.workingCalendar.status.working': 'Working day',
  'schedule.workingCalendar.status.short': 'Short day',
  'schedule.workingCalendar.status.nonWorking': 'Non-working day',
  'schedule.workingCalendar.weekend': 'Weekend',
  'schedule.workingCalendar.legend': 'Legend',
  'schedule.workingCalendar.legendMarked': 'Named by source or manual edit',
  'schedule.workingCalendar.daySummary': '{date}: {status}',
  'schedule.workingCalendar.daySummaryWithReason': '{date}: {status} — {reason}',
  'schedule.workingCalendar.monthSummary': '{working} working days, {off} days off',
  'schedule.workingCalendar.markedCount': '{count} marked days',
  'schedule.workingCalendar.status': 'Status',
  'schedule.workingCalendar.reason': 'Reason',
  'schedule.workingCalendar.reasonAction': 'Reason…',
  'schedule.workingCalendar.reasonTitle': 'Why this day differs',
  'schedule.workingCalendar.cancel': 'Cancel',
  'schedule.workingCalendar.saveFailed': 'The working calendar day could not be saved.',
  'schedule.visitTypes.open': 'Manage encounter types in Schedule',

  'patientSchedule.title': 'Patient availability',
  'patientSchedule.subtitle': 'Commitments outside this clinic participate in every conflict check.',
  'patientSchedule.start': 'Start',
  'patientSchedule.end': 'End',
  'patientSchedule.empty': 'No unavailable time is recorded.',

  'schedule.board.loading': 'Loading the board',
  'schedule.board.loadFailed': 'The board could not be loaded',
  'schedule.board.empty': 'No lanes to show',
  'schedule.board.emptyDetail': 'No resources are configured for this day.',
  'schedule.board.legendLabel': 'Legend',
  'schedule.board.legendBuffer': 'Buffer',
  'schedule.board.legendLayers': 'Layers',
  'schedule.board.legendActual': 'Actual time',
  'schedule.board.legendNow': 'Now',
  'schedule.legend.available': "The owner's open hours, under everything else in the lane: visits are booked on top of them.",
  'schedule.legend.extra': 'Hours added to the usual ones for this day, drawn like open hours but in blue.',
  'schedule.legend.block': 'Hours taken away from the usual ones, hatched in red: nothing is booked on them.',
  'schedule.legend.appointment': 'A booked visit: a filled card in the lane, labelled with the patient and its state.',
  'schedule.legend.hold': 'Time reserved for a visit not yet confirmed: a dashed outline with no fill.',
  'schedule.legend.busy': 'The owner is busy elsewhere: a grey card, so the time is taken without a visit here.',
  'schedule.legend.blockedTime': 'Time blocked off in this lane: a red card carrying its reason.',
  'schedule.legend.mutual': 'Everyone the planner selected is free here: a dashed corridor across every lane.',
  'schedule.legend.patientBusy': 'The selected patient has something else at this time: a purple hatch across the lanes.',
  'schedule.legend.buffer': 'The setup and clean-up time a resource reserves around a visit: dotted edges beyond the card.',
  'schedule.legend.actual': 'When a started visit really ran: a rail along the start edge of the lane.',
  'schedule.legend.now': "The clinic's clock right now: a line across every lane, with its time on the ruler.",
  'schedule.board.mode': 'View',
  'schedule.board.modeReception': 'Front desk',
  'schedule.board.modeCanvas': 'Canvas',
  'schedule.board.modeHint': 'Front desk: one column per resource, fixed slots of the chosen step, a card per visit. Canvas: the free lanes at any pitch.',
  'schedule.board.legendShow': 'Show the legend',
  'schedule.board.legendHide': 'Hide the legend',
  'schedule.reception.label': 'Front desk board',
  'schedule.reception.slot': 'Free at {time}',
  'schedule.reception.arrived': 'Arrived',
  'schedule.reception.inProgress': 'In progress',
  'schedule.reception.done': 'Done',
  'schedule.reception.late': 'Late',
  'schedule.reception.lateBy': 'Late by {minutes} min',
  'schedule.reception.open': 'Open hours',
  'schedule.reception.closed': 'Closed',
  'schedule.reception.legendPayment': 'Payment category',
  'schedule.reception.legendTime': 'Time',
  'schedule.reception.legendPlanned': 'A booked visit nobody has checked in yet: the patient in bold, the visit type and its length under the name, no mark on the end edge.',
  'schedule.reception.legendArrived': 'The patient is in the clinic: a green tick on the end edge.',
  'schedule.reception.legendInProgress': 'The visit has started: a pulse mark.',
  'schedule.reception.legendDone': 'The visit is over: a ticked calendar, and the card is dimmed.',
  'schedule.reception.legendLate': 'The clinic\'s clock has passed the start and nobody is checked in: a red clock mark, and the card says by how many minutes.',
  'schedule.reception.legendNoShow': 'The patient did not attend: a red crossed mark and a struck-through name.',
  'schedule.reception.legendStripe': "The stripe on a card's start edge is the visit type's colour, the same for that type in every column.",
  'schedule.reception.legendOpen': 'The resource is open: white cells, each free one naming its clock. A tap on a cell books that slot.',
  'schedule.reception.legendClosed': 'Outside the resource\'s hours: grey cells with no clock, nothing to book.',
  'schedule.reception.legendBlock': 'Hours shut by hand inside the shift: a red hatch carrying its reason.',
  'schedule.board.conflict': 'Scheduling conflict',
  'schedule.board.patientUnavailable': 'Patient unavailable',
  'schedule.board.holdUntil': 'Hold until {time}',
  'schedule.board.holdExpiredBadge': 'Hold expired',
  'schedule.board.lockedEvent': 'This event can no longer be moved.',
  'schedule.board.time': 'Time',
  'schedule.board.resource': 'Resource',
  'schedule.board.visitType': 'Visit type',
  'schedule.board.start': 'Start',
  'schedule.board.end': 'End',
  'schedule.board.snapHint': 'Drags, arrow keys and typed times move by this step.',
  'schedule.board.pasteAt': 'Paste at {time}',
  'schedule.board.copied': 'Copied {name}. Right-click a lane to paste it there.',
  'schedule.board.pasteRefused': 'This item cannot be copied here: what a copy needs is not visible on this board.',
  'schedule.board.cancelTitle': 'Cancel this appointment?',
  'schedule.board.cancelBlockTitle': 'Remove this block?',
  'schedule.board.cancelDescription': 'The item leaves the board. Its audit history stays.',
  'schedule.board.keep': 'Keep it',
  'schedule.board.createTitle': 'New in the schedule',
  'schedule.board.createKind': 'What to create',
  'schedule.board.createRange': '{date}, {from}–{to}',
  'schedule.board.createAt': '{date}, {time}',
  'schedule.board.add': 'Add an appointment',
  'schedule.planner.hide': 'Hide the planner',
  // A band's own word and the detail that qualifies it: a blocked reason, a
  // time object's name, an appointment type, a planner lane's owner. One
  // template for all of them, because the mark and the order between the
  // two halves is one decision and it is the translator's.
  'schedule.bandLabel': '{label}: {detail}',
  'schedule.calendar.lanesLabel': 'Day board by resource',
  'schedule.calendar.openEvent': 'Open event',
  'schedule.calendar.appointmentHere': 'New appointment at {time}',
  'schedule.calendar.blockHere': 'Block time at {time}',
  'schedule.calendar.emptySelection': 'The selected participants have no bookable lanes on this day.',
  'schedule.calendar.dayNote': 'Appointments: {appointments}',
  'schedule.calendar.dayNoteConflicts': 'Appointments: {appointments} · conflicts: {conflicts}',
  'schedule.calendar.busyOwner': '{owner}, {what}',
  'schedule.calendar.dayLabel': '{day}, {note}',
  'schedule.planner.title': 'Plan together',
  'schedule.planner.objectTitle': '{from}–{to}, {name}',
  'schedule.planner.hint': 'Choose people, rooms and equipment to overlay their time on the calendar and find shared free windows.',
  'schedule.planner.selected': 'Selected participants',
  'schedule.planner.remove': 'Remove {name}',
  'schedule.planner.clear': 'Clear all',
  'schedule.planner.search': 'Search people and resources',
  'schedule.planner.typeFilter': 'Filter by type',
  'schedule.planner.loading': 'Loading the catalogue',
  'schedule.planner.loadFailed': 'The catalogue could not be loaded',
  'schedule.planner.noMatches': 'Nothing matches this search.',
  'schedule.planner.capReached': 'Up to {count} participants can be overlaid at once.',
  'schedule.planner.chooseOwners': 'Choose at least one participant to see shared free time.',
  'schedule.planner.monthHint': 'Shared free windows are shown on the week and day views.',
  'schedule.planner.windows': 'Shared free time',
  'schedule.planner.searching': 'Searching for shared time',
  'schedule.planner.searchFailed': 'The shared time search failed',
  'schedule.planner.blocked': 'No shared free time for: {names}',
  'schedule.planner.noWindows': 'No shared free windows in this range.',
  'schedule.planner.book': 'Book',
  'schedule.planner.mutual': 'Everyone is free',
  'schedule.planner.projectionFailed': 'Participant calendars could not be loaded',
  'schedule.find.patient': 'Patient',
  'schedule.find.type': 'Appointment type',
  'schedule.find.date': 'Day to search',
  'schedule.find.choose': 'Choose…',
  'schedule.find.requirements': 'Requirements',
  'schedule.find.anyResource': 'Any suitable resource',
  'schedule.find.missingInput': 'Choose a patient, an appointment type and a visit reason to search.',
  'schedule.find.patientAvailability': 'Patient availability',
  'schedule.find.searching': 'Searching for openings',
  'schedule.find.failed': 'The search could not be completed',
  'schedule.find.empty': 'No openings match',
  'schedule.find.emptyDetail': 'Try another day or loosen the resource choices.',
  'schedule.find.candidates': 'Suggested times',
  'schedule.find.hold': 'Hold {time}',
  'schedule.requirement.required': 'Required',
  'schedule.requirement.preferred': 'Preferred',
  'schedule.requirement.contextOnly': 'Context only',
  'schedule.panel.title': 'Schedule item',
  'schedule.panel.loading': 'Loading the event',
  'schedule.panel.loadFailed': 'The event could not be loaded',
  'schedule.panel.planned': 'Planned',
  'schedule.panel.actual': 'Actual',
  'schedule.panel.arrived': 'Arrived {time}',
  'schedule.panel.markArrived': 'Mark arrived',
  'schedule.panel.reservations': 'Reserved resources',
  'schedule.panel.buffers': 'Buffers {pre} + {post} min',
  'schedule.panel.reservationReleased': 'Released',
  'schedule.panel.holdExpires': 'Hold expires at {time}',
  'schedule.panel.newStart': 'New start time',
  'schedule.panel.save': 'Save new time',
  'schedule.panel.cancelEvent': 'Cancel appointment',
  'schedule.panel.cancelBlock': 'Cancel block',
  'schedule.panel.cancelReason': 'Cancellation reason',
  'schedule.panel.confirmHold': 'Confirm hold',
  'schedule.panel.releaseHold': 'Release hold',
  'schedule.atRisk.resourceBlocked': 'Resource blocked',
  'schedule.atRisk.resourceInactive': 'Resource inactive',
  'schedule.atRisk.availabilityChanged': 'Availability changed',
  'schedule.atRisk.capacityConflict': 'Capacity conflict',
  'schedule.reception.payment': 'Payment',
  'schedule.reception.payment.personal': 'Self-pay',
  'schedule.reception.payment.insurance': 'Insurance',
  'schedule.reception.payment.program': 'Programme',
  'schedule.reception.payment.unspecified': 'Not stated',
  'schedule.reception.paymentHint': 'What the patient said about paying — a statement for the front desk. It sets no price, checks no coverage, records no debt and marks nothing paid.',
  'schedule.reception.comment': 'Booking comment',
  'schedule.reception.checked': 'Checked',
  'schedule.reception.confirmed': 'Confirmed',
  'schedule.reception.contactCompleted': 'Contact completed',
  'schedule.reception.wantsAnotherTime': 'Wants another time',
  'schedule.reception.minor': 'Minor',
  'schedule.reception.debt': 'Owes the clinic',
  'schedule.reception.hasComment': 'Has a comment',
  'schedule.impact.foundAt': 'Conflict found at the check of {time}',
  'schedule.impact.detail': '{reasons} — found by the last automatic check, at {time}. Nothing has re-checked this visit since; its own move or cancellation clears it.',
  'schedule.finance.title': 'Account',
  'schedule.finance.receivable': 'Outstanding',
  'schedule.finance.availableFunds': 'Available funds',
  'schedule.finance.prepaidValue': 'Prepaid value',
  'schedule.finance.asOf': 'As of {time}',
  'schedule.finance.noAccount': 'No own account',
  'schedule.finance.unavailable': 'Financial facts are unavailable',
  'schedule.finance.loading': 'Reading the account',
  'schedule.finance.open': 'Open in Finance',
  'schedule.finance.noVisitClaim': 'These are the account’s facts, not a statement about this visit.',
  'schedule.finance.openAccount': 'Open the account',
  'schedule.finance.packages': 'Packages',
  'schedule.finance.packagesLoading': 'Reading the packages',
  'schedule.finance.packagesUnavailable': 'Packages are unavailable',
  'schedule.finance.packagesUndetermined': 'Packages cannot be determined: no own account names a person',
  'schedule.finance.packagesNone': 'No active packages',
  'schedule.finance.packagePurchased': 'Purchased',
  'schedule.finance.packageRemaining': 'Remaining',
  'schedule.finance.packageConsumed': 'Used',
  'schedule.finance.packagesMore': 'More packages are listed in Finance.',
  'schedule.finance.openPackages': 'Open prepaid packages',
  'schedule.onlineBooking.title': 'Online booking',
  'schedule.onlineBooking.loading': 'Reading the offers',
  'schedule.onlineBooking.undetermined': 'Undetermined',
  'schedule.onlineBooking.none': 'Not offered for any active visit type',
  'schedule.onlineBooking.offeredFor': 'Offered for {types}',
  'schedule.onlineBooking.factLoading': 'Online booking for this visit type with this practitioner: reading the offers',
  'schedule.onlineBooking.factOffered': 'Online booking for this visit type with this practitioner is offered',
  'schedule.onlineBooking.factNotOffered': 'Online booking for this visit type with this practitioner is not offered',
  'schedule.onlineBooking.factUnknown': 'Online booking for this visit type with this practitioner is undetermined',
  'schedule.combinations.title': 'Combination',
  'schedule.combinations.none': 'No combination',
  'schedule.combinations.manage': 'Combinations',
  'schedule.combinations.panelTitle': 'Resource combinations',
  'schedule.combinations.intro': 'A practitioner, a room and equipment booked together. A combination narrows the board to its members and starts a new visit with them chosen; every booking is still checked in full.',
  'schedule.combinations.saved': 'Saved combinations',
  'schedule.combinations.empty': 'No combinations yet',
  'schedule.combinations.use': 'Use',
  'schedule.combinations.inUse': 'In use',
  'schedule.combinations.remove': 'Remove the combination {name}',
  'schedule.combinations.new': 'New combination',
  'schedule.combinations.name': 'Name',
  'schedule.combinations.noMember': 'None',
  'schedule.combinations.needTwo': 'Name it and choose at least two members.',
  'schedule.combinations.limit': 'At most {max} combinations can be saved.',
  'schedule.combinations.save': 'Save the combination',
  'schedule.combinations.unknownResource': 'Resource no longer in the catalogue',
  'schedule.combinations.unsaved': 'A new resource combination',
  'schedule.combinations.loadFailed': 'The saved combinations could not be read.',
  'schedule.reception.legendMarks': 'Visit marks',
  'schedule.reception.legendPayPersonal': 'The patient said they pay themselves: a green card.',
  'schedule.reception.legendPayInsurance': 'The patient named an insurer: a blue card.',
  'schedule.reception.legendPayProgram': 'The visit is under a programme: a purple card.',
  'schedule.reception.legendPayUnspecified': 'Nothing stated yet: a grey card. The fill is what the patient said, not a price, a cover or a payment.',
  'schedule.reception.legendConflict': 'The last automatic check found a conflict with this visit: a red warning mark, and the card says when it was found.',
  'schedule.reception.legendDebt': 'The patient\'s account owes the clinic: an amber coins mark. A fact of the account, not of this visit.',
  'schedule.reception.legendMinor': 'The patient is under eighteen on the civil day: the mark reads <18.',
  'schedule.reception.legendComment': 'The front desk left a comment on the visit: a speech mark.',
  'schedule.reception.legendContact': 'The patient was reached about this visit: a green handset.',
  'schedule.reception.legendAnotherTime': 'The patient asked for another time: an amber calendar mark.',
  'schedule.reception.minorMark': '<18',
  'schedule.reception.state': 'Status',
  'schedule.reception.resources': 'Resources',
  'schedule.reception.resourceInfo': 'About {name}',
  'schedule.reception.workplaces': 'Workplaces',
  'schedule.reception.hours': 'Hours today',
  'schedule.reception.noHours': 'No shift today',
  'schedule.reception.load': 'Load',
  'schedule.reception.loadValue': '{booked} of {available} min, {percent}%',
  'schedule.reception.visits': 'Visits',
  'schedule.reception.moveEarlier': 'Move column earlier',
  'schedule.reception.moveLater': 'Move column later',
  'schedule.reception.weekLabel': 'Front desk week',
  'schedule.reception.weekCell': '{resource}, {day}: {visits} visits',
  'schedule.reception.weekLoad': '{booked} of {available} min',
  'schedule.reception.weekVisits': 'Visits: {count}',
  'schedule.reception.weekConflicts': 'Conflicts: {count}',
  'schedule.reception.weekPercent': '{percent}%',
  'schedule.reception.filters': 'Board filters',
  'schedule.reception.specialty': 'Specialty',
  'schedule.reception.specialtyAll': 'All specialties',
  'schedule.reception.search': 'Search resources',
  'schedule.reception.typesAll': 'All types',
  'schedule.board.patientChart': 'Patient chart',
  'schedule.board.patientSchedule': 'Patient’s schedule',
  'schedule.board.moveStart': 'Move',
  'schedule.board.moving': 'Moving {name}. Right-click a slot to put it there.',
  'schedule.board.moveAt': 'Move here, {time}',
  'schedule.board.clipCancel': 'Clear',
  'schedule.board.toWaiting': 'Add to the waiting list',
} as const
